import React, { useState, useEffect } from 'react';
import { getApiEndpoint, API_BASE_URL } from '../../../../config/api';
import './Walkways.css';
import { toast } from 'react-toastify';
import AssetDetailModal from '../shared/AssetDetailModal';
import { openLatestTestReport } from '../shared/openLatestTestReport';
import { isSuperAdmin } from '../../../../utils/roleUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEdit, faTrash, faSearch, faFilter, faRoad, faPlus, faTimes, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';

const getBadgeClass = (status) => {
  const key = (status || 'unknown').toLowerCase();
  const map = {
    pass: 'rs-badge-pass', passed: 'rs-badge-passed',
    fail: 'rs-badge-fail', failed: 'rs-badge-failed',
    pending: 'rs-badge-pending', overdue: 'rs-badge-overdue',
    unknown: 'rs-badge-unknown'
  };
  return `rs-badge ${map[key] || 'rs-badge-unknown'}`;
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  try {
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? 'N/A' : dt.toLocaleDateString('en-GB');
  } catch {
    return 'N/A';
  }
};

const WALKWAY_TYPES = ['Standard', 'Access', 'Maintenance', 'Emergency', 'Non-Slip'];

const EMPTY_FORM = {
  name: '',
  location: '',
  type: 'Standard',
  width: '',
  material: '',
  surface: '',
  installedBy: '',
  dateInstalled: new Date().toISOString().split('T')[0],
  lastTestDate: new Date().toISOString().split('T')[0],
  nextTestDate: '',
  status: 'Unknown',
  notes: ''
};

export default function Walkways({ propertyId }) {
  const [walkways, setWalkways] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWalkway, setEditingWalkway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [newWalkway, setNewWalkway] = useState({ ...EMPTY_FORM });
  const [detailAsset, setDetailAsset] = useState(null);

  const fetchWalkwaysData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/walkways`), {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error(`Failed to fetch walkways: ${response.status}`);

      const data = await response.json();
      setWalkways((Array.isArray(data) ? data : [data]).map(formatWalkway));
    } catch (err) {
      console.error('Error fetching walkways:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to fetch walkways');
    } finally {
      setLoading(false);
    }
  };

  const formatWalkway = (walkway) => ({
    id: walkway.id,
    propertyId: walkway.property_id,
    name: walkway.walkway_name || walkway.name || `Walkway ${walkway.id}`,
    location: walkway.location_description || walkway.location || 'N/A',
    type: walkway.type || walkway.walkway_type || 'Standard',
    status: walkway.status || 'Unknown',
    lastTestDate: walkway.last_inspection_date,
    nextTestDate: walkway.next_inspection_date,
    width: walkway.width ? `${walkway.width}` : walkway.width_mm ? `${parseFloat(walkway.width_mm) / 1000}m` : 'N/A',
    material: walkway.material || 'N/A',
    surface: walkway.surface_type || walkway.surface || 'N/A',
    installedBy: walkway.installed_by || 'N/A',
    dateInstalled: walkway.date_installed,
    notes: walkway.notes || '',
    test_report_url: walkway.test_report_url
  });

  useEffect(() => {
    if (propertyId) fetchWalkwaysData();
  }, [propertyId]);

  // Auto-open asset from drawing hotspot navigation
  useEffect(() => {
    const handler = (e) => {
      const { assetId, assetTable } = e.detail || {};
      if (assetTable === 'walkways' && assetId && walkways.length) {
        const asset = walkways.find(a => a.id === parseInt(assetId));
        if (asset) setDetailAsset(asset);
      }
    };
    window.addEventListener('openAssetFromHotspot', handler);
    return () => window.removeEventListener('openAssetFromHotspot', handler);
  }, [walkways]);

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!showFilterDropdown) return;
    const handler = (e) => {
      if (!e.target.closest('.rs-filter-dropdown') && !e.target.closest('.filter-toggle-btn')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterDropdown]);

  const handleViewTestData = (walkway) => {
    openLatestTestReport({
      propertyId,
      assetType: 'walkway',
      assetId: walkway.id,
      fallbackAsset: walkway,
      emptyMessage: 'No test data available for this walkway',
      onEmpty: (msg) => toast.info(msg),
    });
  };

  const handleCreateWalkway = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/walkways`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walkway_name: newWalkway.name,
          location_description: newWalkway.location,
          type: newWalkway.type,
          width: newWalkway.width || '',
          material: newWalkway.material || '',
          surface: newWalkway.surface || '',
          installed_by: newWalkway.installedBy,
          date_installed: newWalkway.dateInstalled,
          last_inspection_date: newWalkway.lastTestDate,
          next_inspection_date: newWalkway.nextTestDate,
          status: newWalkway.status,
          notes: newWalkway.notes || ''
        })
      });

      if (!response.ok) throw new Error('Failed to create walkway');

      const createdWalkway = await response.json();
      setWalkways(prev => [...prev, formatWalkway(createdWalkway)]);
      setNewWalkway({ ...EMPTY_FORM });
      setShowCreateModal(false);
      toast.success('Walkway created successfully');
    } catch (err) {
      console.error('Error creating walkway:', err);
      toast.error('Failed to create walkway');
    }
  };

  const handleEditWalkway = async () => {
    if (!editingWalkway) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/walkways/${editingWalkway.id}`), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walkway_name: editingWalkway.name,
          location_description: editingWalkway.location,
          type: editingWalkway.type,
          width: editingWalkway.width || '',
          material: editingWalkway.material || '',
          surface: editingWalkway.surface || '',
          installed_by: editingWalkway.installedBy,
          date_installed: editingWalkway.dateInstalled,
          last_inspection_date: editingWalkway.lastTestDate,
          next_inspection_date: editingWalkway.nextTestDate,
          status: editingWalkway.status,
          notes: editingWalkway.notes || ''
        })
      });

      if (!response.ok) throw new Error('Failed to update walkway');

      const updatedWalkway = await response.json();
      setWalkways(prev => prev.map(w => w.id === editingWalkway.id ? formatWalkway(updatedWalkway) : w));
      setShowEditModal(false);
      setEditingWalkway(null);
      toast.success('Walkway updated successfully');
    } catch (err) {
      console.error('Error updating walkway:', err);
      toast.error('Failed to update walkway');
    }
  };

  const handleDeleteWalkway = async (walkwayId) => {
    if (!window.confirm('Are you sure you want to delete this walkway?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/walkways/${walkwayId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to delete walkway');
      setWalkways(prev => prev.filter(w => w.id !== walkwayId));
      toast.success('Walkway deleted successfully');
    } catch (err) {
      console.error('Error deleting walkway:', err);
      toast.error('Failed to delete walkway');
    }
  };

  // Filtered data
  const filtered = walkways
    .filter(w => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return [w.name, w.location, w.type, w.status, w.material, w.surface]
        .some(v => v && v.toLowerCase().includes(q));
    })
    .filter(w => typeFilter === 'All' || w.type === typeFilter);

  // ─── Error State ───
  if (error) {
    return (
      <div className="walkways-section">
        <h1>Walkways</h1>
        <div className="rs-error-alert">
          <FontAwesomeIcon icon={faRoad} />
          <span>{error}</span>
          <button onClick={fetchWalkwaysData}>Retry</button>
        </div>
      </div>
    );
  }

  // ─── Loading State ───
  if (loading) {
    return (
      <div className="walkways-section">
        <h1>Walkways</h1>
        <div className="rs-skeleton">
          {[...Array(6)].map((_, i) => <div key={i} className="rs-skeleton-row" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="walkways-section">
      <h1>Walkways</h1>

      {/* Toolbar */}
      <div className="rs-toolbar">
        <div className="rs-search-bar">
          <FontAwesomeIcon icon={faSearch} className="rs-search-icon" />
          <input
            type="text"
            placeholder="Search walkways..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table / Cards */}
      <div className="rs-table-card">
        {/* Desktop Table */}
        <div className="rs-table-scroll">
          <table className="rs-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Location</th>
                <th>Type</th>
                <th>Status</th>
                <th>Last Test</th>
                <th>Next Test</th>
                <th>Width</th>
                <th>Material</th>
                <th>Surface</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="11">
                    <div className="rs-empty-state">
                      <div className="rs-empty-icon"><FontAwesomeIcon icon={faRoad} /></div>
                      <h3 className="rs-empty-title">
                        {walkways.length === 0 ? 'No walkways yet' : 'No results found'}
                      </h3>
                      <p className="rs-empty-desc">
                        {walkways.length === 0
                          ? 'Add your first walkway to start tracking roof safety compliance.'
                          : 'Try adjusting your search or filter criteria.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map(walkway => (
                <tr key={walkway.id}>
                  <td title={walkway.id}>{walkway.id}</td>
                  <td title={walkway.name}>{walkway.name}</td>
                  <td title={walkway.location}>{walkway.location}</td>
                  <td>{walkway.type}</td>
                  <td>
                    <span className={getBadgeClass(walkway.status)}>
                      {walkway.status || 'Unknown'}
                    </span>
                  </td>
                  <td>{formatDate(walkway.lastTestDate)}</td>
                  <td>{formatDate(walkway.nextTestDate)}</td>
                  <td>{walkway.width}</td>
                  <td>{walkway.material}</td>
                  <td>{walkway.surface}</td>
                  <td>
                    <div className="rs-actions">
                      <button className="rs-action-btn" onClick={() => handleViewTestData(walkway)} title="View Test Data" aria-label="View test data">
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button className="rs-action-btn" onClick={() => setDetailAsset(walkway)} title="Test History" aria-label="View test history">
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                      </button>
                      {isSuperAdmin() && (<>
                      <button className="rs-action-btn" onClick={() => { setEditingWalkway({ ...walkway }); setShowEditModal(true); }} title="Edit" aria-label="Edit walkway">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="rs-action-btn rs-action-delete" onClick={() => handleDeleteWalkway(walkway.id)} title="Delete" aria-label="Delete walkway">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                      </>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="rs-mobile-cards">
          {filtered.length === 0 ? (
            <div className="rs-empty-state">
              <div className="rs-empty-icon"><FontAwesomeIcon icon={faRoad} /></div>
              <h3 className="rs-empty-title">
                {walkways.length === 0 ? 'No walkways yet' : 'No results found'}
              </h3>
              <p className="rs-empty-desc">
                {walkways.length === 0
                  ? 'Add your first walkway to start tracking.'
                  : 'Adjust your search or filters.'}
              </p>
            </div>
          ) : filtered.map(walkway => (
            <div key={walkway.id} className="rs-mobile-card">
              <div className="rs-mobile-card-header">
                <span className="rs-mobile-card-title">{walkway.name}</span>
                <span className={getBadgeClass(walkway.status)}>{walkway.status || 'Unknown'}</span>
              </div>
              <div className="rs-mobile-card-meta">
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Location</span>
                  <span className="rs-mobile-card-value">{walkway.location}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Type</span>
                  <span className="rs-mobile-card-value">{walkway.type}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Last Test</span>
                  <span className="rs-mobile-card-value">{formatDate(walkway.lastTestDate)}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Next Test</span>
                  <span className="rs-mobile-card-value">{formatDate(walkway.nextTestDate)}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Width</span>
                  <span className="rs-mobile-card-value">{walkway.width}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Material</span>
                  <span className="rs-mobile-card-value">{walkway.material}</span>
                </div>
              </div>
              <div className="rs-mobile-card-actions">
                <button className="rs-btn rs-btn-secondary" onClick={() => handleViewTestData(walkway)}>
                  <FontAwesomeIcon icon={faEye} /> View
                </button>
                <button className="rs-btn rs-btn-secondary" onClick={() => setDetailAsset(walkway)}>
                  <FontAwesomeIcon icon={faClockRotateLeft} /> Test History
                </button>
                {isSuperAdmin() && (<>
                <button className="rs-btn rs-btn-secondary" onClick={() => { setEditingWalkway({ ...walkway }); setShowEditModal(true); }}>
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </button>
                <button className="rs-btn rs-btn-danger" onClick={() => handleDeleteWalkway(walkway.id)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
                </>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="rs-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}>
          <div className="rs-modal" role="dialog" aria-modal="true" aria-label="Create walkway">
            <div className="rs-modal-header">
              <h2>Create Walkway</h2>
              <button className="rs-modal-close" onClick={() => setShowCreateModal(false)} aria-label="Close modal">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleCreateWalkway(); }}>
              <div className="rs-modal-body">
                <div className="rs-form-grid">
                  <div className="rs-form-group">
                    <label>Walkway Name</label>
                    <input
                      type="text"
                      value={newWalkway.name}
                      onChange={(e) => setNewWalkway({ ...newWalkway, name: e.target.value })}
                      placeholder="e.g., WK-001"
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={newWalkway.location}
                      onChange={(e) => setNewWalkway({ ...newWalkway, location: e.target.value })}
                      placeholder="e.g., Main Roof Access Path"
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Type</label>
                    <select
                      value={newWalkway.type}
                      onChange={(e) => setNewWalkway({ ...newWalkway, type: e.target.value })}
                    >
                      {WALKWAY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Installed By</label>
                    <input
                      type="text"
                      value={newWalkway.installedBy}
                      onChange={(e) => setNewWalkway({ ...newWalkway, installedBy: e.target.value })}
                      placeholder="e.g., SafeAccess Ltd"
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Date Installed</label>
                    <input
                      type="date"
                      value={newWalkway.dateInstalled}
                      onChange={(e) => setNewWalkway({ ...newWalkway, dateInstalled: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Width</label>
                    <input
                      type="text"
                      value={newWalkway.width}
                      onChange={(e) => setNewWalkway({ ...newWalkway, width: e.target.value })}
                      placeholder="e.g., 1.2m"
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Material</label>
                    <input
                      type="text"
                      value={newWalkway.material}
                      onChange={(e) => setNewWalkway({ ...newWalkway, material: e.target.value })}
                      placeholder="e.g., Steel, Aluminum"
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Surface</label>
                    <input
                      type="text"
                      value={newWalkway.surface}
                      onChange={(e) => setNewWalkway({ ...newWalkway, surface: e.target.value })}
                      placeholder="e.g., Non-slip, Grating"
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Status</label>
                    <select
                      value={newWalkway.status}
                      onChange={(e) => setNewWalkway({ ...newWalkway, status: e.target.value })}
                    >
                      <option value="Unknown">Unknown</option>
                      <option value="Pass">Pass</option>
                      <option value="Fail">Fail</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Last Test Date</label>
                    <input
                      type="date"
                      value={newWalkway.lastTestDate}
                      onChange={(e) => setNewWalkway({ ...newWalkway, lastTestDate: e.target.value })}
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Next Test Date</label>
                    <input
                      type="date"
                      value={newWalkway.nextTestDate}
                      onChange={(e) => setNewWalkway({ ...newWalkway, nextTestDate: e.target.value })}
                    />
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label>Notes</label>
                    <textarea
                      value={newWalkway.notes}
                      onChange={(e) => setNewWalkway({ ...newWalkway, notes: e.target.value })}
                      rows="3"
                      placeholder="Additional notes..."
                    />
                  </div>
                </div>
              </div>
              <div className="rs-modal-footer">
                <button type="button" className="rs-btn rs-btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="rs-btn rs-btn-primary">
                  Create Walkway
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingWalkway && (
        <div className="rs-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowEditModal(false); setEditingWalkway(null); } }}>
          <div className="rs-modal" role="dialog" aria-modal="true" aria-label="Edit walkway">
            <div className="rs-modal-header">
              <h2>Edit Walkway</h2>
              <button className="rs-modal-close" onClick={() => { setShowEditModal(false); setEditingWalkway(null); }} aria-label="Close modal">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleEditWalkway(); }}>
              <div className="rs-modal-body">
                <div className="rs-form-grid">
                  <div className="rs-form-group">
                    <label>Walkway Name</label>
                    <input
                      type="text"
                      value={editingWalkway.name}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={editingWalkway.location}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, location: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Type</label>
                    <select
                      value={editingWalkway.type}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, type: e.target.value })}
                    >
                      {WALKWAY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Installed By</label>
                    <input
                      type="text"
                      value={editingWalkway.installedBy}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, installedBy: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Date Installed</label>
                    <input
                      type="date"
                      value={editingWalkway.dateInstalled || ''}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, dateInstalled: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Width</label>
                    <input
                      type="text"
                      value={editingWalkway.width}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, width: e.target.value })}
                      placeholder="e.g., 1.2m"
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Material</label>
                    <input
                      type="text"
                      value={editingWalkway.material}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, material: e.target.value })}
                      placeholder="e.g., Steel, Aluminum"
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Surface</label>
                    <input
                      type="text"
                      value={editingWalkway.surface}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, surface: e.target.value })}
                      placeholder="e.g., Non-slip, Grating"
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Status</label>
                    <select
                      value={editingWalkway.status}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, status: e.target.value })}
                    >
                      <option value="Unknown">Unknown</option>
                      <option value="Pass">Pass</option>
                      <option value="Fail">Fail</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Last Test Date</label>
                    <input
                      type="date"
                      value={editingWalkway.lastTestDate || ''}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, lastTestDate: e.target.value })}
                    />
                  </div>
                  <div className="rs-form-group">
                    <label>Next Test Date</label>
                    <input
                      type="date"
                      value={editingWalkway.nextTestDate || ''}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, nextTestDate: e.target.value })}
                    />
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label>Notes</label>
                    <textarea
                      value={editingWalkway.notes}
                      onChange={(e) => setEditingWalkway({ ...editingWalkway, notes: e.target.value })}
                      rows="3"
                      placeholder="Additional notes..."
                    />
                  </div>
                </div>
              </div>
              <div className="rs-modal-footer">
                <button type="button" className="rs-btn rs-btn-secondary" onClick={() => { setShowEditModal(false); setEditingWalkway(null); }}>
                  Cancel
                </button>
                <button type="submit" className="rs-btn rs-btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AssetDetailModal
        isOpen={!!detailAsset}
        onClose={() => setDetailAsset(null)}
        asset={detailAsset ? { ...detailAsset, name: detailAsset.name || detailAsset.walkway_name, location: detailAsset.location } : null}
        assetType="walkway"
        propertyId={propertyId}
        assetMeta={detailAsset ? [
          { label: 'Type', value: detailAsset.type },
          { label: 'Width', value: detailAsset.width },
          { label: 'Material', value: detailAsset.material },
          { label: 'Surface', value: detailAsset.surface }
        ] : []}
      />
    </div>
  );
}
